import { all, call, put, takeEvery } from "redux-saga/effects";
import * as types from "../actions/actionTypes";
import { getAllUsersResponse, getUserByIdResponse, patchEntityList } from "../actions";
import axios from "axios";
import toast from "react-hot-toast";
import { axiosPatch, axiosPost } from "../../utils/common/axios";
import { API_BASE_URL, CLIENT_ID } from "../../auth/config";
import {
  createEntitySagas,
  errorMessage,
  extractPayloadAndCallback,
  notifyError,
  notifySuccess,
  runOptimisticAction,
  selectCompanyId,
  toQuery,
} from "./sagaHelpers";

export const userEntity = createEntitySagas({
  entity: "user",
  endpoint: "users",
  label: "User",
  scope: "company",
  injectCompanyId: true,
  computedFields: ["brands"],
  selectSlice: (state) => state.user,
  listKey: "users",
  types: {
    getAll: types.GET_ALL_USERS,
    getById: types.GET_USER_BY_ID,
    create: types.CREATE_USER,
    update: types.UPDATE_USER,
    remove: types.DELETE_USER,
  },
  actions: { getAllResponse: getAllUsersResponse, getByIdResponse: getUserByIdResponse },
});

// Registers the login account on the SSO server so the invited user can sign in.
function* registerSsoAccount(user, sso) {
  try {
    const { data } = yield call(axios.post, `${API_BASE_URL}/api/users/register`, {
      username: user.name,
      email: user.email,
      password: sso.password,
      mobileNumber: user.mobileNumber ? Number(String(user.mobileNumber).replace(/\D/g, '')) : null,
      companyId: user.companyId,
      status: 1,
      roles: [sso.roleId],
    });
    if (data?.id) yield call(axios.post, `${API_BASE_URL}/api/user-access`, { userId: data.id, clientId: CLIENT_ID });
  } catch (e) {
    toast.error(`SSO login account not created: ${errorMessage(e, 'unknown error')}`);
  }
}

// payload: { name, email, mobileNumber, role, brandIds: number[], sso?: { password, roleId } }
function* inviteSaga(action) {
  const { payload, callback } = extractPayloadAndCallback(action);
  const { brandIds = [], sso, ...user } = payload;
  const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    const body = { ...user, companyId: user.companyId || (yield* selectCompanyId(user)) };
    if (sso?.password) yield* registerSsoAccount(body, sso);
    yield put(patchEntityList("user", "prepend", { item: { ...body, id: tempId, status: "Active", invite: "Pending", brands: [], __optimistic: true } }));
    const { data } = yield call(axiosPost, `users/invite${toQuery({ brandIds })}`, body);
    yield put(patchEntityList("user", "replace", { id: tempId, item: data }));
    notifySuccess("User invited");
    callback?.success?.(data);
  } catch (e) {
    yield put(patchEntityList("user", "remove", { id: tempId }));
    notifyError(e, "Could not invite user");
    callback?.failure?.(e);
  }
}

// payload: { id, status: "Active" | "Suspended" }
function* statusSaga(action) {
  const { payload, callback } = extractPayloadAndCallback(action);
  const { id, status } = payload;
  yield* runOptimisticAction({
    entity: "user",
    selectList: userEntity.selectList,
    id,
    changes: { status },
    request: () => axiosPatch(`users/${id}/status${toQuery({ status })}`),
    successMessage: status === "Suspended" ? "User suspended" : "User reactivated",
    errorFallback: "Could not change user status",
    callback,
  });
}

export function* userSaga() {
  yield all([
    call(userEntity.watcher),
    takeEvery(types.INVITE_USER, inviteSaga),
    takeEvery(types.UPDATE_USER_STATUS, statusSaga),
  ]);
}
